import React, {useEffect, useRef, useState} from "react";
import {useIntl} from "react-intl";
import {useDarkMode} from "../hooks/useDarkMode";
import {TipCurrency} from "../model/constants";
import {
    ApplePayInitParamsResponse,
    ShareKeyType,
    applePayInitParams,
    initializePayment,
    processPayment,
    validateSession,
} from "../model/paymentApi";
import {getBrowserData, handlePaymentProcess} from "../model/paymentUtils";
import {getLanguage} from "../translations";
import {Fingerprint} from "./Fingerprint";

function formatPaymentParams(
    initParams: ApplePayInitParamsResponse,
    price: string,
    currency: TipCurrency
): ApplePayJS.ApplePayPaymentRequest {
    return {
        countryCode: initParams.initParams.countryCode,
        supportedNetworks: initParams.initParams.supportedNetworks,
        merchantCapabilities: initParams.initParams.merchantCapabilities,
        currencyCode: currency,
        total: {label: initParams.displayName, amount: price},
    };
}

export function ApplePay({
    price,
    currency,
    setShowApplePay,
    shareKey,
    shareKeyType,
}: {
    price: string;
    currency: TipCurrency;
    setShowApplePay: (value: boolean) => void;
    shareKey: string;
    shareKeyType: ShareKeyType;
}) {
    const [paymentParams, setPaymentParams] = useState<ApplePayInitParamsResponse | null>(null);
    const fingerPrintRef = useRef(null);
    const isDark = useDarkMode();
    const intl = useIntl();

    useEffect(() => {
        applePayInitParams(getLanguage())
            .then((response) => setPaymentParams(response))
            .catch(() => setShowApplePay(false));
    }, [setShowApplePay]);

    if (!paymentParams) {
        return null;
    }

    const handleLoad = () => {
        const sessionRequest: ApplePayJS.ApplePayPaymentRequest = formatPaymentParams(
            paymentParams,
            price,
            currency
        );

        const applePaySession = new ApplePaySession(3, sessionRequest);

        applePaySession.onvalidatemerchant = (event) => {
            validateSession({
                validationUri: event.validationURL,
                language: getLanguage(),
                tenant: "LIFTAGO",
            })
                .then((merchantSession) =>
                    applePaySession.completeMerchantValidation(
                        JSON.parse(merchantSession.sessionData)
                    )
                )
                .catch((error) => {
                    console.error(error);
                    alert(intl.formatMessage({id: "generic_error"}));
                });
        };

        applePaySession.onpaymentauthorized = (event) => {
            initializePayment({
                shareKey: shareKey,
                shareKeyType: shareKeyType,
                redirectUri: window.location.href,
                price: {amount: parseInt(price), ccy: currency},
                paymentDataBase64: btoa(JSON.stringify(event.payment.token.paymentData)),
            })
                .then((response) => {
                    applePaySession.completePayment(window.ApplePaySession.STATUS_SUCCESS);
                    if (response.fingerPrintUri && fingerPrintRef.current) {
                        const fingerPrintIframe: HTMLIFrameElement = fingerPrintRef.current;
                        fingerPrintIframe.src = response.fingerPrintUri;
                    }
                    if (response.trackingTipId) {
                        processPayment({
                            trackingTipId: response.trackingTipId,
                            browserJsData: getBrowserData(),
                        }).then((response) => {
                            handlePaymentProcess(response);
                        });
                    }
                })
                .catch((error) => {
                    applePaySession.completePayment(window.ApplePaySession.STATUS_FAILURE);
                    console.error(error);
                    alert(intl.formatMessage({id: "generic_error"}));
                });
        };

        applePaySession.begin();
    };

    return (
        <div>
            <button
                data-cy="tracking-tip-apple-pay-btn"
                className={`apple-pay-button ${
                    isDark ? "apple-pay-button-white" : "apple-pay-button-black"
                }`}
                onClick={handleLoad}
            ></button>
            <Fingerprint ref={fingerPrintRef} />
        </div>
    );
}
